/**
 * The groups round trip (#1393 R2): Coursework's audience opens People's
 * groups with history state naming the exact work it left, so Return steps
 * Back to that work with its draft and group revision as they were.
 */
const KEY = "groupsOrigin";
const RETURNED = "groupsReturned";

export interface GroupsOrigin {
  courseId: string;
  /** The exact work's address: this course's page with one work or teaching-draft visit. */
  href: string;
  /** The work's title as the teacher last wrote it. */
  title: string;
  /** The router's history index of that address, when the router keeps one. */
  idx?: number;
}

export function groupsOriginState(origin: GroupsOrigin): Record<string, GroupsOrigin> {
  return { [KEY]: origin };
}

/** The state the origin entry keeps, so returning to it focuses Manage groups again. */
export function groupsReturnState(state: unknown): Record<string, unknown> {
  return { ...(state && typeof state === "object" ? state : {}), [RETURNED]: true };
}

export function groupsReturned(state: unknown): boolean {
  return Boolean(state && typeof state === "object" && (state as Record<string, unknown>)[RETURNED] === true);
}

/** Whether `href` is a local address of `pathname` that opens one work or teaching draft. */
export function exactWorkAddress(href: string, pathname: string): boolean {
  if (!href.startsWith("/") || href.startsWith("//") || href.includes("\\")) return false;
  let url: URL;
  try { url = new URL(href, "http://origin.invalid"); } catch { return false; }
  if (url.origin !== "http://origin.invalid" || url.pathname !== pathname) return false;
  const visits = url.searchParams.getAll("visit");
  return visits.length === 1 && /^(work|teaching-draft):[^,]+$/.test(visits[0]);
}

/**
 * The origin this entry was opened from: this course's own page (the one
 * People's groups is drawn on) with one exact work open. Anything else names
 * no origin, so no Return is offered.
 */
export function groupsOrigin(state: unknown, courseId: string, pathname: string): GroupsOrigin | null {
  const origin = state && typeof state === "object" ? (state as Record<string, unknown>)[KEY] : null;
  if (!origin || typeof origin !== "object") return null;
  const { courseId: course, href, title, idx } = origin as Record<string, unknown>;
  if (course !== courseId || typeof href !== "string" || typeof title !== "string") return null;
  if (idx !== undefined && !Number.isInteger(idx)) return null;
  if (!exactWorkAddress(href, pathname)) return null;
  return { courseId, href, title, ...(idx === undefined ? {} : { idx: idx as number }) };
}

/** The router's index of the current history entry, when it keeps one. */
export function historyIndex(): number | undefined {
  const idx = (window.history.state as { idx?: unknown } | null)?.idx;
  return Number.isInteger(idx) ? idx as number : undefined;
}
